import { useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ImageDropzone } from "@/components/ui/image-dropzone";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useUpsertCategory } from "@/hooks/use-categories";
import { describeCallableError } from "@/lib/callables";
import { normalize } from "@/lib/format";
import { DEFAULT_PRODUCT_TYPE_NAME, isReservedProductTypeName } from "@/lib/productTypes";
import {
  ACCEPTED_IMAGE_TYPES,
  CATEGORY_IMAGE_MAX_INPUT_BYTES,
  describeUploadError,
  uploadCategoryImage,
} from "@/lib/upload";
import type { Category } from "@/types";

/** O slug é derivado do nome, e não digitado: dois slugs iguais quebram a busca. */
function slugify(name: string): string {
  return normalize(name).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function CategoryDialog({
  category,
  nextOrder,
  open,
  onOpenChange,
}: {
  category?: Category;
  nextOrder: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const upsert = useUpsertCategory();
  const isEdit = Boolean(category?.id);

  const [form, setForm] = useState(() => ({
    name: category?.name ?? "",
    description: category?.description ?? "",
    imageUrl: category?.imageUrl ?? "",
    icon: category?.icon ?? "",
    order: String(category?.order ?? nextOrder),
    isActive: category?.isActive ?? true,
  }));
  // A imagem só sobe ao salvar: escolher um arquivo e cancelar o diálogo não
  // pode deixar arquivo solto no Storage.
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const busy = upsert.isPending || uploadProgress != null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) {
      toast.error("O nome do tipo de produto é obrigatório.");
      return;
    }
    // O backend recusa de novo; aqui é para a pessoa saber antes de enviar imagem.
    if (isReservedProductTypeName(name)) {
      toast.error(`"${DEFAULT_PRODUCT_TYPE_NAME}" é o tipo padrão e já existe.`);
      return;
    }
    const slug = category?.slug || slugify(name);

    let imageUrl = form.imageUrl.trim();
    if (imageFile) {
      try {
        setUploadProgress(0);
        imageUrl = await uploadCategoryImage(imageFile, slug, setUploadProgress);
      } catch (error) {
        toast.error(describeUploadError(error));
        return;
      } finally {
        setUploadProgress(null);
      }
    }

    try {
      await upsert.mutateAsync({
        id: category?.id,
        name,
        slug,
        description: form.description.trim() || undefined,
        // Vazio na edição apaga a imagem; a callable troca por remoção do campo.
        imageUrl: imageUrl || (isEdit ? "" : undefined),
        icon: form.icon.trim() || undefined,
        order: Number(form.order) || nextOrder,
        isActive: form.isActive,
      });
      toast.success(isEdit ? "Tipo de produto atualizado." : "Tipo de produto criado.");
      onOpenChange(false);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar tipo de produto" : "Novo tipo de produto"}</DialogTitle>
          <DialogDescription>
            Os tipos são fixos do projeto e valem para todos os estabelecimentos. O vendedor escolhe
            o tipo de cada produto, e o cliente usa os tipos para buscar.
          </DialogDescription>
        </DialogHeader>

        <form id="category-form" onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cat-name">Nome</Label>
            <Input
              id="cat-name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
              autoFocus
            />
            {!isEdit && form.name ? (
              <p className="text-caption text-muted-foreground">
                Endereço na busca: <span className="font-mono">{slugify(form.name)}</span>
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cat-desc">Descrição</Label>
            <Textarea
              id="cat-desc"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          <ImageDropzone
            label="Imagem"
            currentUrl={form.imageUrl || null}
            file={imageFile}
            onFileChange={setImageFile}
            onRemoveCurrent={() => setForm((p) => ({ ...p, imageUrl: "" }))}
            accept={ACCEPTED_IMAGE_TYPES}
            maxBytes={CATEGORY_IMAGE_MAX_INPUT_BYTES}
            disabled={busy}
            progress={uploadProgress}
            onReject={(message) => toast.error(message)}
          />

          <div className="space-y-1.5 sm:max-w-32">
            <Label htmlFor="cat-order">Ordem</Label>
            <Input
              id="cat-order"
              type="number"
              min={0}
              value={form.order}
              onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))}
            />
          </div>

          <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
            <span className="min-w-0">
              <span className="block text-small font-semibold">Ativo</span>
              <span className="block text-caption text-muted-foreground">
                Tipo inativo sai da busca do aplicativo do cliente. Os produtos dele continuam no
                cardápio de cada estabelecimento.
              </span>
            </span>
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => setForm((p) => ({ ...p, isActive: v }))}
            />
          </label>
        </form>

        <DialogFooter>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="category-form" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" /> : null}
            {uploadProgress != null
              ? `Enviando imagem ${Math.round(uploadProgress * 100)}%`
              : isEdit
                ? "Salvar"
                : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
